/**
 * Convert Quill-produced HTML to plain text with deterministic rules.
 * The output is byte-identical to the Python port in
 * `server/emailbison/rendering/render.py:render_html_to_plain_text`.
 *
 * Semantic: one Enter typed in the composer maps to one `\n` in the output.
 * Quill represents an Enter as a closed block tag (`<p>`, `<div>`, etc.) and
 * an empty paragraph (the user pressing Enter on a blank line) as
 * `<p><br></p>`. Both close with a single newline. The `<br>` inside an
 * otherwise-empty block is structural — needed for browser rendering but
 * carrying no text — so we skip it. This matches what users see in Gmail
 * and other normal email clients.
 */

const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'blockquote',
])

function isStructurallyEmpty(el: HTMLElement): boolean {
  return (el.textContent ?? '').trim() === ''
}

export function renderHtmlToPlainText(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
  const out: string[] = []

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? '')
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    if (tag === 'br') {
      out.push('\n')
      return
    }
    if (tag === 'a') {
      const href = el.getAttribute('href') ?? ''
      const text = el.textContent ?? ''
      out.push(text === href || !href ? text : `${text} (${href})`)
      return
    }
    if (tag === 'li') {
      out.push('- ')
      for (const child of Array.from(el.childNodes)) walk(child)
      out.push('\n')
      return
    }

    const isBlock = BLOCK_TAGS.has(tag)
    if (isBlock && isStructurallyEmpty(el)) {
      out.push('\n')
      return
    }
    for (const child of Array.from(el.childNodes)) walk(child)
    if (isBlock) out.push('\n')
  }

  for (const child of Array.from(doc.body.childNodes)) walk(child)

  // NBSP (U+00A0) -> regular space. Google Docs's clipboard format uses NBSP
  // between words; preserving them prevents the preview pane from wrapping.
  //
  // How EmailBison handles plain-text emails: at send time Bison converts
  // the stored body to plaintext and collapses any run of 2+ blank lines
  // down to a single blank line. Multiple typed Enters on empty lines
  // cannot be preserved end-to-end no matter what we send (NBSP/ZWSP
  // padding gets normalized away inside Bison's pipeline). Cap here so
  // the in-app preview matches what the recipient actually sees: one
  // Enter -> single newline, two or more blank lines -> exactly one
  // blank line (`\n\n`).
  return out
    .join('')
    .replace(/\u00A0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+$/, '')
}
